import { useHomeComposerAutosuggest } from '#pplx/frontend/apps/ask/ask-composer/internal/composer-sdk-plugins/useHomeComposerAutosuggest.js';
import { useHomeComposerChinPlugins } from '#pplx/frontend/apps/ask/ask-composer/internal/composer-sdk-plugins/useHomeComposerChinPlugins.js';
import { useHomeComposerComputerMenuPlugins } from '#pplx/frontend/apps/ask/ask-composer/internal/composer-sdk-plugins/useHomeComposerComputerMenuPlugins.js';
import { useHomeComposerInputHeaderPlugins } from '#pplx/frontend/apps/ask/ask-composer/internal/composer-sdk-plugins/useHomeComposerInputHeaderPlugins.js';
import { useAskSourceSuggestionsPlugin } from '#pplx/frontend/apps/ask/ask-input/source-suggestions/useAskSourceSuggestionsPlugin.js';
import { useAskComposerDictationPlugins } from '#pplx/frontend/apps/ask/composer-sdk-plugins/dictation/useAskComposerDictationPlugins.js';
import { useAskComposerFooterControls } from '#pplx/frontend/apps/ask/composer-sdk-plugins/footer-controls/useAskComposerFooterControls.js';
import { useAskComposerPlusMenuPlugins } from '#pplx/frontend/apps/ask/composer-sdk-plugins/plus-menu/useAskComposerPlusMenuPlugins.js';
import { useAskComposerUploadAffordance } from '#pplx/frontend/apps/ask/composer-sdk-plugins/search-mode-toggle/useAskComposerUploadAffordance.js';
import { useAskSearchModePlugins } from '#pplx/frontend/apps/ask/composer-sdk-plugins/search-mode/useAskSearchModePlugins.js';
import { useAskComposerVoiceModePlugin } from '#pplx/frontend/apps/ask/composer-sdk-plugins/voice-mode/useAskComposerVoiceModePlugin.js';
import { AskDeepResearchModeToggle } from '#pplx/frontend/apps/ask/hooks/ask-deep-research-hooks/AskDeepResearchModeToggle.js';
import { useGlobalAvailableSources } from '#pplx/frontend/apps/ask/hooks/shared-source-selection-hooks/useSharedSourceSelection.js';
import { SearchMode } from '#pplx/frontend/ask-models/types/search-modes.js';
import { EMPTY_ARRAY } from '#pplx/frontend/common/defaults.js';
import { askLinkPlugin } from '#pplx/frontend/composer-sdk-plugins/ask-link/askLinkPlugin.js';
import { serializedStateToMarkdown } from '#pplx/frontend/composer-sdk/serializedStateToMarkdown.js';
import { createElement, useCallback, useMemo } from 'react';
const homeLinkPlugin = askLinkPlugin();
export function useAskComposerSdkPlugins({ surfaceConfig, attachments, autosuggestControls, composerRef, pageIntegration: pageProps, composerValue, isSubmitDisabled, onDictationSubmit, characterCount, isVisible, project, onProjectChange, onFocus, onInsertMention, onRemoveMention, remoteBoxPlugin, ...modeOptions }) {
    const value = useMemo(() => serializedStateToMarkdown(composerValue), [composerValue]);
    const dictation = useAskComposerDictationPlugins({
        attachments,
        composerValue,
        isSubmitDisabled,
        onDictationSubmit,
        showStopButton: false,
        value,
    });
    const voiceModePlugin = useAskComposerVoiceModePlugin({
        hasQuery: dictation.hasQuery,
        isFollowUp: false,
        isTranscribing: dictation.isTranscribing,
        showStopButton: false,
    });
    const autosuggest = useHomeComposerAutosuggest({
        autosuggestEnabled: surfaceConfig.autosuggestEnabled,
        querySource: surfaceConfig.querySource,
        ...autosuggestControls,
        composerRef,
        isTranscribing: dictation.isTranscribing,
    });
    const { onClick: onPageClick } = pageProps;
    const { onComposerClick: onAutosuggestClick } = autosuggest;
    const onComposerClick = useCallback(() => {
        onPageClick();
        onAutosuggestClick();
    }, [onAutosuggestClick, onPageClick]);
    const pageIntegration = useMemo(() => ({ ...pageProps, onClick: onComposerClick }), [onComposerClick, pageProps]);
    const handleSourceMentionInsert = useCallback((mention) => composerRef.current?.insertMention(mention) ?? 'unavailable', [composerRef]);
    const sourceSuggestionsPlugin = useAskSourceSuggestionsPlugin({
        onInsertMention: handleSourceMentionInsert,
        selectedSearchMode: autosuggestControls.selectedSearchMode,
        showSources: pageIntegration.searchModeControls.enabled && !import.meta.env.VITE_IS_SIDECAR_BUILD,
    });
    const { sources, setSources } = useGlobalAvailableSources();
    const plusMenuPlugins = useAskComposerPlusMenuPlugins({
        composerRef,
        cloudFiles: attachments.cloudMenuProps,
        inputJson: composerValue,
        sources,
        onSourcesChange: setSources,
        initialSources: EMPTY_ARRAY,
        isComputer: autosuggestControls.selectedSearchMode === SearchMode.ASI,
        isFollowUp: false,
        showLeftAttribution: pageIntegration.searchModeControls.enabled && !import.meta.env.VITE_IS_SIDECAR_BUILD,
        showSources: pageIntegration.searchModeControls.enabled && !import.meta.env.VITE_IS_SIDECAR_BUILD,
    });
    const computerMenu = useHomeComposerComputerMenuPlugins({
        reason: surfaceConfig.reason,
        onSearchModeSelected: modeOptions.onSearchModeSelected,
        searchModeControls: pageIntegration.searchModeControls,
        omitDeviceMentions: remoteBoxPlugin !== null,
    });
    const { projectPlugin, launchersPlugin } = useHomeComposerChinPlugins({
        projectSearchSource: surfaceConfig.projectSearchSource,
        isVisible,
        showFreePreviewLabel: true,
        project,
        onProjectChange,
        onFocus,
        onInsertMention,
        onRemoveMention,
    });
    const renderUploadToggle = useAskComposerUploadAffordance({
        attachments: attachments.fileAttachments,
        surface: surfaceConfig.upsellSurface,
    });
    const renderToggle = useCallback((props) => createElement(AskDeepResearchModeToggle, null, renderUploadToggle(props)), [renderUploadToggle]);
    const modePlugins = useAskSearchModePlugins({
        ...modeOptions,
        appearance: pageIntegration.composerProps.appearance,
        onFocus,
        searchModeControls: pageIntegration.searchModeControls,
        isFollowUp: false,
        querySource: surfaceConfig.querySource,
        hasImageAttachments: attachments.hasImageAttachments,
        renderToggle,
        computerCommandMenu: computerMenu.typeahead?.commandMenu,
        showModelSelector: dictation.isTranscribing ? false : undefined,
        modelSelectorOverrides: dictation.isTranscribing
            ? undefined
            : modeOptions.modelSelectorOverrides,
        showComputerModelChip: dictation.isTranscribing ? false : undefined,
    });
    const inputHeaderPlugins = useHomeComposerInputHeaderPlugins({
        composerRef,
        characterCount,
    });
    const footerControls = useAskComposerFooterControls({ showIncognitoHint: true });
    const plugins = useMemo(() => [
        ...computerMenu.plugins,
        homeLinkPlugin,
        attachments.plugin,
        ...plusMenuPlugins,
        ...footerControls.beforeModelControls,
        ...modePlugins,
        ...(sourceSuggestionsPlugin ? [sourceSuggestionsPlugin] : []),
        ...dictation.plugins,
        voiceModePlugin,
        ...computerMenu.suggestionPlugins,
        ...autosuggest.plugins,
        ...inputHeaderPlugins,
        ...(projectPlugin ? [projectPlugin] : []),
        ...(remoteBoxPlugin ? [remoteBoxPlugin] : []),
        ...(launchersPlugin ? [launchersPlugin] : []),
    ], [
        attachments.plugin,
        autosuggest.plugins,
        computerMenu.plugins,
        computerMenu.suggestionPlugins,
        dictation.plugins,
        footerControls.beforeModelControls,
        inputHeaderPlugins,
        modePlugins,
        projectPlugin,
        remoteBoxPlugin,
        launchersPlugin,
        voiceModePlugin,
        plusMenuPlugins,
        sourceSuggestionsPlugin,
    ]);
    const typeahead = useMemo(() => ({
        ...computerMenu.typeahead,
        overlayPlacement: computerMenu.typeahead?.overlayPlacement ?? autosuggest.overlayPlacement,
        wrapNavigation: false,
    }), [autosuggest.overlayPlacement, computerMenu.typeahead]);
    return useMemo(() => ({
        autosuggest,
        pageIntegration,
        plugins,
        typeahead,
        isComputerCommandMenuOpen: computerMenu.isComputerCommandMenuOpen,
        isTranscribing: dictation.isTranscribing,
        placeholder: dictation.placeholder,
        stopTranscriptionIfActive: dictation.stopTranscriptionIfActive,
    }), [
        autosuggest,
        pageIntegration,
        plugins,
        typeahead,
        computerMenu.isComputerCommandMenuOpen,
        dictation.isTranscribing,
        dictation.placeholder,
        dictation.stopTranscriptionIfActive,
    ]);
}
//# sourceMappingURL=useAskComposerSdkPlugins.js.map